/**
 * Eventos destacados del landing: qué se muestra, en qué fase está cada uno y
 * qué se puede guardar.
 *
 * Todo trabaja con fechas de CALENDARIO (`YYYY-MM-DD`). «Hoy» lo pone quien
 * llama, en la zona del seminario (`todayInZoneWeb`): un visitante de Chile a
 * las 23:00 no puede estar ya en el día siguiente. Las diferencias se calculan
 * con `Date.UTC`, que no tiene cambios de horario: un día siempre mide 24 h.
 */
import type { FeaturedEvent } from '@dosfilos/domain';

export const MAX_FEATURED_EVENTS = 5;

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

function utcDay(ymd: string): number | null {
  const m = YMD.exec(ymd);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Días de calendario de `from` a `to` (negativo si `to` es anterior). */
export function daysBetween(from: string, to: string): number {
  const a = utcDay(from);
  const b = utcDay(to);
  if (a === null || b === null) return NaN;
  return Math.round((b - a) / 86_400_000);
}

/**
 * La URL de inscripción abre otro sitio desde un landing público: solo se
 * acepta `https:`. Cualquier otra cosa (`javascript:`, `http:`, una ruta
 * relativa) no se pinta.
 */
export function safeExternalUrl(raw: string | null | undefined): string | null {
  const value = raw?.trim();
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname ? url.href : null;
  } catch {
    return null;
  }
}

/**
 * La imagen de la tarjeta sale del Storage del seminario (https). En local, el
 * emulador de Storage sirve por http en localhost, y eso también se acepta.
 */
export function safeImageUrl(raw: string | null | undefined): string | null {
  const value = raw?.trim();
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol === 'https:' && url.hostname) return url.href;
    const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    return url.protocol === 'http:' && local ? url.href : null;
  } catch {
    return null;
  }
}

export type EventPhase =
  | { kind: 'upcoming'; days: number }
  | { kind: 'today' }
  | { kind: 'live' }
  | { kind: 'ended' };

export function eventPhase(
  event: Pick<FeaturedEvent, 'startDate' | 'endDate'>,
  today: string,
): EventPhase {
  const untilStart = daysBetween(today, event.startDate);
  if (untilStart > 0) return { kind: 'upcoming', days: untilStart };
  if (untilStart === 0) return { kind: 'today' };
  const untilEnd = daysBetween(today, event.endDate || event.startDate);
  return untilEnd >= 0 ? { kind: 'live' } : { kind: 'ended' };
}

/** Por qué un evento se ve o no — lo que el editor le explica al director. */
export type EventVisibility = 'visible' | 'hidden' | 'scheduled' | 'ended' | 'invalid';

export function eventVisibility(event: FeaturedEvent, today: string): EventVisibility {
  if (validateFeaturedEvent(event).length > 0) return 'invalid';
  if (!event.active) return 'hidden';
  if (eventPhase(event, today).kind === 'ended') return 'ended';
  if (event.visibleFrom && daysBetween(today, event.visibleFrom) > 0) return 'scheduled';
  return 'visible';
}

/** Los eventos que el landing muestra hoy, del más próximo al más lejano. */
export function visibleFeaturedEvents(
  events: readonly FeaturedEvent[] | null | undefined,
  today: string,
): FeaturedEvent[] {
  return (events ?? [])
    .filter((e) => eventVisibility(e, today) === 'visible')
    .sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0))
    .slice(0, MAX_FEATURED_EVENTS);
}

export type FeaturedEventError = 'title' | 'startDate' | 'endDate' | 'registrationUrl';

/** Claves de error del editor; vacío = se puede guardar. */
export function validateFeaturedEvent(event: FeaturedEvent): FeaturedEventError[] {
  const errors: FeaturedEventError[] = [];
  if (!event.title?.trim()) errors.push('title');
  if (utcDay(event.startDate ?? '') === null) errors.push('startDate');
  else if (event.endDate && !(daysBetween(event.startDate, event.endDate) >= 0)) {
    errors.push('endDate');
  }
  if (!safeExternalUrl(event.registrationUrl)) errors.push('registrationUrl');
  return errors;
}

/**
 * Lo que se guarda: textos recortados y opcionales vacíos OMITIDOS. Firestore
 * no acepta `undefined`, y un solo campo así hace fallar el guardado entero de
 * Configuración.
 */
export function normalizeFeaturedEvents(events: readonly FeaturedEvent[]): FeaturedEvent[] {
  return events.slice(0, MAX_FEATURED_EVENTS).map((e) => {
    const description = e.description?.trim();
    const endDate = e.endDate?.trim();
    const price = e.price?.trim();
    const visibleFrom = e.visibleFrom?.trim();
    const imageUrl = safeImageUrl(e.imageUrl);
    return {
      id: e.id,
      label: e.label.trim(),
      title: e.title.trim(),
      startDate: e.startDate,
      venue: e.venue.trim(),
      registrationUrl: e.registrationUrl.trim(),
      active: e.active,
      ...(description ? { description } : {}),
      ...(endDate && endDate !== e.startDate ? { endDate } : {}),
      ...(price ? { price } : {}),
      ...(visibleFrom ? { visibleFrom } : {}),
      ...(imageUrl ? { imageUrl } : {}),
    };
  });
}

export function newFeaturedEvent(id: string): FeaturedEvent {
  return {
    id,
    label: '',
    title: '',
    startDate: '',
    venue: '',
    registrationUrl: '',
    active: true,
  };
}
